// Session controller (authentication spec: Session behaviour; design D4).
// - Access token in memory only; a rotating refresh token in sessionStorage.
// - A new tab signs in with a top-level prompt=none redirect (no iframes).
// - Sign-out in one tab signs out all tabs (BroadcastChannel).
// The OIDC library is behind AuthBackend, so every scenario is testable without a provider.

export type Session = { accessToken: string; expiresAt: number; subject: string; email?: string | undefined }

export type SignInFailure = "provider_error" | "cancelled"

export interface AuthBackend {
  /** Completes a redirect sign-in if the current URL is a callback; otherwise returns null. */
  completeRedirect(url: string): Promise<Session | null>
  /** Whether a refresh token is stored for this tab. */
  hasStoredSession(): Promise<boolean>
  /** Exchanges the stored refresh token (rotated by the provider) for a new access token. */
  refresh(): Promise<Session>
  redirectToSignIn(options: { prompt?: "none" }): Promise<void>
  endProviderSession(): Promise<void>
  removeLocal(): Promise<void>
}

export interface Channel {
  post(message: string): void
  subscribe(listener: (message: string) => void): () => void
}

export type AuthState =
  | { status: "loading" }
  | { status: "signed-in"; session: Session }
  | { status: "signed-out"; reason?: "expired" | "signed-out" | undefined }
  | { status: "error"; failure: SignInFailure }

const RENEW_BEFORE_SECONDS = 60
const SIGNED_OUT = "signed-out"

export class OidcRedirectError extends Error {
  constructor(readonly code: string) {
    super(code)
  }
}

export class AuthController {
  private state: AuthState = { status: "loading" }
  private readonly listeners = new Set<() => void>()
  private readonly unsubscribe: () => void

  constructor(
    private readonly backend: AuthBackend,
    private readonly channel: Channel,
    private readonly tabStorage: Pick<Storage, "getItem" | "setItem" | "removeItem">,
    private readonly now: () => number = () => Date.now() / 1000,
  ) {
    this.unsubscribe = channel.subscribe((message) => {
      if (message === SIGNED_OUT) void this.localSignOut("signed-out")
    })
  }

  getState = (): AuthState => this.state

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private set(state: AuthState): void {
    this.state = state
    for (const listener of this.listeners) listener()
  }

  async start(url: string): Promise<void> {
    try {
      const completed = await this.backend.completeRedirect(url)
      if (completed) {
        this.set({ status: "signed-in", session: completed })
        return
      }
    } catch (error) {
      const code = error instanceof OidcRedirectError ? error.code : "server_error"
      if (code === "login_required" || code === "interaction_required") {
        this.set({ status: "signed-out" })
      } else {
        this.set({ status: "error", failure: code === "access_denied" ? "cancelled" : "provider_error" })
      }
      return
    }
    if (await this.backend.hasStoredSession()) {
      try {
        this.set({ status: "signed-in", session: await this.backend.refresh() })
        return
      } catch {
        await this.backend.removeLocal()
      }
    }
    // Login v2 providers may render a raw error for prompt=none without a provider session.
    // A fresh tab therefore presents an explicit sign-in action instead of redirecting blindly.
    this.tabStorage.removeItem("manapp.silent-attempted") // clean up pre-fix sessions
    this.set({ status: "signed-out" })
  }

  async signIn(): Promise<void> {
    await this.backend.redirectToSignIn({})
  }

  async signOut(): Promise<void> {
    this.channel.post(SIGNED_OUT)
    await this.localSignOut("signed-out")
    await this.backend.endProviderSession()
  }

  /** Called when the API answers auth.token_expired (maximum session lifetime reached). */
  async expire(): Promise<void> {
    await this.localSignOut("expired")
  }

  async accessToken(): Promise<string> {
    const state = this.state
    if (state.status !== "signed-in") throw new Error("not signed in")
    if (state.session.expiresAt - this.now() > RENEW_BEFORE_SECONDS) return state.session.accessToken
    const session = await this.backend.refresh()
    this.set({ status: "signed-in", session })
    return session.accessToken
  }

  dispose(): void {
    this.unsubscribe()
  }

  private async localSignOut(reason: "expired" | "signed-out"): Promise<void> {
    await this.backend.removeLocal()
    this.set({ status: "signed-out", reason })
  }
}

export function broadcastChannel(name = "manapp-auth"): Channel {
  const channel = new BroadcastChannel(name)
  return {
    post: (message) => channel.postMessage(message),
    subscribe: (listener) => {
      const handler = (event: MessageEvent<string>) => listener(event.data)
      channel.addEventListener("message", handler)
      return () => channel.removeEventListener("message", handler)
    },
  }
}
